import React, { useEffect, useMemo, useRef, useState } from 'react';
import { notifyManager, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import WidgetPageShell from '@components/common/WidgetPageShell';
import FooterBrand from '@components/common/FooterBrand';
import {
  DEFAULT_INDICES_TAB_VALUE,
  INDICES_TAB_VALUES,
  normalizeIndicesTabValue,
  type IndicesTabValue,
} from '@constants/indicesTabs';
import {
  buildOrderedHtsRics,
  groupIndexQuotesByCountry,
  joinIndexQuotesWithHts,
  mapMarketBoardHtsByRic,
  mapMarketBoardQuotes,
} from '@apis/indicesMappers';
import { fetchMarketBoardHts, fetchMarketBoardQuotes } from '@apis/indices';
import { buildSelectedRicRefreshSnapshots } from '@apis/indicesSelectedRicRefresh';
import {
  getApiParamErrorNotificationMessage,
  shouldNotifyDataRequestError,
} from '@apis/client';
import { buildMarketBoardHtsQueryKey, useMarketBoardHts } from '@hooks/useMarketBoardHts';
import { buildMarketBoardQuotesQueryKey, useMarketBoardQuotes } from '@hooks/useMarketBoardQuotes';
import {
  INDICES_PERIODS,
  type IndexInstrumentViewModel,
  type IndicesMarketGroup,
  type IndicesPeriod,
} from '@app-types/indices';
import { appendIndicesTabParam, getIframeQueryParamsFromSearch } from '@utils/queryParams';
import { notify } from '@utils/notify';
import { getIndicesChartInterval } from '@utils/indicesChartBundle';
import IndicesChartDialog from './components/IndicesChartDialog';
import IndicesHorizontalViewport from './components/IndicesHorizontalViewport';
import IndicesMarketLayout from './components/IndicesMarketLayout';
import MarketHeader from './components/MarketHeader';
import './IndicesPage.less';

const QUOTES_ERROR_MESSAGE = 'マーケット情報の取得に失敗しました。';
const HTS_ERROR_MESSAGE = 'チャートデータの取得に失敗しました。';
const REFRESH_ERROR_MESSAGE = 'マーケット情報の更新に失敗しました。';

const INDICES_FOOTNOTES: Record<IndicesTabValue, {
  introduction: string;
  lines: readonly string[];
}> = {
  [INDICES_TAB_VALUES.global]: {
    introduction: '【更新頻度】海外主要指標の各指標の更新タイミングは下記の通りです。',
    lines: [
      '●為替(ドル円、ユーロドル、ポンド円、ポンドドル、メキシコペソ円) 7時3分頃',
      '●NYダウ 7時40分頃 [夏6時40分頃]',
      '●NASDAQ 8時3分頃 [夏7時3分頃]',
      '●先物(CME225円、CME225ドル、NY金、WTI原油　10分遅延更新(現地時間表示)',
      '●CAC40 10時3分頃 [夏9時3分頃]',
      '●債券（米10＆30年債、英&独&仏10年債) 16時25分頃',
    ],
  },
  [INDICES_TAB_VALUES.asia]: {
    introduction: '【更新頻度】アジア株指数の各指標の更新タイミングは下記の通りです。',
    lines: [
      '●上海総合指数　16時30分頃',
      '●香港・ハンセン、レッドチップ、指数、香港中国企業指数　16時30分頃',
      '●全普通株指数　20分遅延更新(現地時間表示)',
    ],
  },
};

const IndicesPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tab = normalizeIndicesTabValue(
    getIframeQueryParamsFromSearch(location.search).tab,
  ) ?? DEFAULT_INDICES_TAB_VALUE;
  const mktGroup: IndicesMarketGroup = tab === INDICES_TAB_VALUES.global ? 'world' : 'asia';
  const [period, setPeriod] = useState<IndicesPeriod>(INDICES_PERIODS.daily);
  const [selectedInstrumentId, setSelectedInstrumentId] = useState<string | null>(null);
  const [refreshRevision, setRefreshRevision] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const refreshInFlightRef = useRef(false);
  const mountedRef = useRef(true);

  const quotesQuery = useMarketBoardQuotes(mktGroup);
  const quotes = useMemo(() => mapMarketBoardQuotes(quotesQuery.data), [quotesQuery.data]);
  const orderedRics = useMemo(() => buildOrderedHtsRics(quotes), [quotes]);
  const interval = getIndicesChartInterval(period);
  const htsQuery = useMarketBoardHts(
    mktGroup,
    interval,
    orderedRics,
    quotesQuery.isSuccess,
  );
  const htsByRic = useMemo(() => mapMarketBoardHtsByRic(htsQuery.data), [htsQuery.data]);
  const htsState = htsQuery.isError
    ? 'error'
    : htsQuery.isPending
      ? 'loading'
      : 'success';
  const instruments = useMemo(
    () => joinIndexQuotesWithHts(quotes, htsByRic, htsState),
    [htsByRic, htsState, quotes],
  );
  const countries = useMemo(() => groupIndexQuotesByCountry(instruments), [instruments]);
  const selectedInstrument = selectedInstrumentId
    ? instruments.find(({ id }) => id === selectedInstrumentId)
    : undefined;
  const visiblePopupRef = useRef({ selectedInstrumentId, mktGroup, interval });
  visiblePopupRef.current = { selectedInstrumentId, mktGroup, interval };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!quotesQuery.error || !shouldNotifyDataRequestError(quotesQuery.error)) return;
    notify({
      level: 'error',
      message: getApiParamErrorNotificationMessage(quotesQuery.error) ?? QUOTES_ERROR_MESSAGE,
    });
  }, [quotesQuery.error]);

  useEffect(() => {
    if (!htsQuery.error || !shouldNotifyDataRequestError(htsQuery.error)) return;
    notify({
      level: 'error',
      message: getApiParamErrorNotificationMessage(htsQuery.error) ?? HTS_ERROR_MESSAGE,
    });
  }, [htsQuery.error]);

  useEffect(() => {
    if (selectedInstrumentId && quotesQuery.isSuccess && !selectedInstrument) {
      setSelectedInstrumentId(null);
    }
  }, [quotesQuery.isSuccess, selectedInstrument, selectedInstrumentId]);

  const handleSelectTab = (nextTab: IndicesTabValue) => {
    if (nextTab === tab) return;
    setSelectedInstrumentId(null);
    navigate({
      pathname: location.pathname,
      search: appendIndicesTabParam(location.search, nextTab),
    });
  };

  const handleSelectInstrument = (
    instrument: IndexInstrumentViewModel,
    trigger: HTMLElement,
  ) => {
    returnFocusRef.current = trigger;
    setSelectedInstrumentId(instrument.id);
    setRefreshRevision(0);
  };

  const handleRefresh = async () => {
    if (!selectedInstrument || !quotesQuery.isSuccess || !htsQuery.isSuccess || refreshInFlightRef.current) return;
    const context = {
      ric: selectedInstrument.ric,
      mktGroup,
      interval,
      orderedRics,
      selectedInstrumentId: selectedInstrument.id,
    };
    const quotesKey = buildMarketBoardQuotesQueryKey(context.mktGroup);
    const htsKey = buildMarketBoardHtsQueryKey(
      context.mktGroup,
      context.interval,
      context.orderedRics,
    );

    refreshInFlightRef.current = true;
    setIsRefreshing(true);
    try {
      const [quotesResult, htsResult] = await Promise.allSettled([
        fetchMarketBoardQuotes({ mktGroup: context.mktGroup }),
        fetchMarketBoardHts({ rics: [context.ric], interval: context.interval }),
      ]);
      if (quotesResult.status === 'rejected') throw quotesResult.reason;
      if (htsResult.status === 'rejected') throw htsResult.reason;
      const snapshots = buildSelectedRicRefreshSnapshots({
        ric: context.ric,
        currentQuotes: queryClient.getQueryData(quotesKey),
        refreshedQuotes: quotesResult.value,
        currentHts: queryClient.getQueryData(htsKey),
        refreshedHts: htsResult.value,
      });

      notifyManager.batch(() => {
        queryClient.setQueryData(quotesKey, snapshots.quotes);
        queryClient.setQueryData(htsKey, snapshots.hts);
      });
      const visible = visiblePopupRef.current;
      if (
        mountedRef.current
        && visible.selectedInstrumentId === context.selectedInstrumentId
        && visible.mktGroup === context.mktGroup
        && visible.interval === context.interval
      ) {
        setRefreshRevision((revision) => revision + 1);
      }
    } catch (caught) {
      if (shouldNotifyDataRequestError(caught)) {
        notify({
          level: 'error',
          message: getApiParamErrorNotificationMessage(caught) ?? REFRESH_ERROR_MESSAGE,
        });
      }
    } finally {
      refreshInFlightRef.current = false;
      if (mountedRef.current) setIsRefreshing(false);
    }
  };

  const isQuotesLoading = quotesQuery.isLoading
    || (quotesQuery.isFetching && !quotesQuery.data);
  const hasQuotesError = quotesQuery.isError && !quotesQuery.data;
  const isQuotesEmpty = quotesQuery.isSuccess && quotes.length === 0;
  const footnotes = INDICES_FOOTNOTES[tab];

  return (
    <WidgetPageShell className="indices-page">
      <main className="indices-page__main">
        <div className="indices-page__header">
          <MarketHeader
            activeTab={tab}
            onSelectTab={handleSelectTab}
            activePeriod={period}
            onSelectPeriod={setPeriod}
          />
        </div>

        {isQuotesLoading ? (
          <div className="indices-page__state-shell">
            <div className="indices-page__state" role="status" aria-busy="true">
              データを読み込んでいます…
            </div>
          </div>
        ) : hasQuotesError ? (
          <div className="indices-page__state-shell">
            <div className="indices-page__state" role="alert">
              データの取得に失敗しました。
            </div>
          </div>
        ) : isQuotesEmpty ? (
          <div className="indices-page__state-shell">
            <div className="indices-page__state" role="status">
              データがありません。
            </div>
          </div>
        ) : (
          <IndicesHorizontalViewport resetKey={mktGroup}>
            <div className="indices-page__canvas">
              <IndicesMarketLayout
                mktGroup={mktGroup}
                countries={countries}
                period={period}
                onSelectInstrument={handleSelectInstrument}
              />
            </div>
          </IndicesHorizontalViewport>
        )}
        <section className="indices-page__footnotes" aria-label="更新頻度">
          <p className="indices-page__footnotes-introduction">{footnotes.introduction}</p>
          <p className="indices-page__footnotes-content">{footnotes.lines.join(' ')}</p>
        </section>
      </main>
      <div className="indices-page__footer">
        <FooterBrand />
      </div>
      {selectedInstrument && (
        <IndicesChartDialog
          instrument={selectedInstrument}
          period={period}
          refreshRevision={refreshRevision}
          isRefreshing={isRefreshing}
          canRefresh={quotesQuery.isSuccess && htsQuery.isSuccess}
          returnFocusRef={returnFocusRef}
          onSelectPeriod={setPeriod}
          onRefresh={handleRefresh}
          onClose={() => setSelectedInstrumentId(null)}
        />
      )}
    </WidgetPageShell>
  );
};

export default IndicesPage;
